import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Calendar, FolderOpen } from 'lucide-react';
import { getPartyAlbums, getPartyAlbum } from '../services/api';
import { PageLayout } from './layouts/PageLayout';
import { Navigation } from './Navigation';
import { Footer } from './Footer';

const PartyGallery = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    if (albumId) {
      fetchAlbum();
    } else {
      fetchAlbums();
    }
  }, [albumId]);

  const fetchAlbums = async () => {
    setLoading(true);
    try {
      const response = await getPartyAlbums();
      setAlbums(response.data);
    } catch (error) {
      console.error('Error fetching party albums:', error);
    }
    setLoading(false);
  };

  const fetchAlbum = async () => {
    setLoading(true);
    try {
      const response = await getPartyAlbum(albumId);
      setCurrentAlbum(response.data);
    } catch (error) {
      console.error('Error fetching party album:', error);
      navigate('/gallery/party');
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const openLightbox = (index) => {
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const goToPrevious = () => {
    if (currentAlbum?.photos) {
      setSelectedIndex((prev) => (prev === 0 ? currentAlbum.photos.length - 1 : prev - 1));
    }
  };

  const goToNext = () => {
    if (currentAlbum?.photos) {
      setSelectedIndex((prev) => (prev === currentAlbum.photos.length - 1 ? 0 : prev + 1));
    }
  };

  // Render album list (folders)
  if (!albumId) {
    return (
      <>
        <Navigation />
        <PageLayout>
          <div className="min-h-screen pt-32 pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Header */}
              <div className="mb-12">
                <Link
                  to="/gallery"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 transition-all duration-300 mb-6 group"
                >
                  <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
                  <span className="group-hover:tracking-wide transition-all duration-300">Back to Galleries</span>
                </Link>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold group cursor-default">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-white transition-all duration-300 group-hover:tracking-wider">
                    Party Gallery
                  </span>
                </h1>
                <p className="text-gray-400 mt-4 text-lg">Experience our events and party vibes</p>
              </div>

              {/* Albums Grid */}
              {loading ? (
                <div className="text-center text-gray-400 py-20">Loading albums...</div>
              ) : albums.length === 0 ? (
                <div className="text-center text-gray-400 py-20">
                  <FolderOpen size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-xl">No party albums yet.</p>
                  <p className="text-sm mt-2">Check back soon for updates!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {albums.map((album) => (
                    <Link
                      key={album.id}
                      to={`/gallery/party/${album.id}`}
                      className="group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-xl bg-gray-900 hover:scale-[1.02] transition-all duration-300 border border-gray-800 hover:border-purple-500/50"
                    >
                      {/* Cover Image */}
                      {album.cover_image ? (
                        <img
                          src={album.cover_image}
                          alt={album.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-black">
                          <FolderOpen size={64} className="text-purple-500/50" />
                        </div>
                      )}

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

                      {/* Album Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors duration-300">
                          {album.name}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                          <Calendar size={14} />
                          <span>{formatDate(album.date)}</span>
                        </div>
                        {album.description && (
                          <p className="text-gray-400 text-sm line-clamp-2">{album.description}</p>
                        )}
                        <div className="mt-3 text-purple-400 text-sm font-medium">
                          {album.photos?.length || 0} photos
                        </div>
                      </div>

                      {/* Hover indicator */}
                      <div className="absolute top-4 right-4 bg-purple-500/80 text-white px-3 py-1 rounded-full text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        View Album
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PageLayout>
        <Footer />
      </>
    );
  }

  // Render single album photos
  return (
    <>
      <Navigation />
      <PageLayout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-12">
              <Link
                to="/gallery/party"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-purple-500 transition-all duration-300 mb-6 group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
                <span className="group-hover:tracking-wide transition-all duration-300">Back to Party Albums</span>
              </Link>
              
              {currentAlbum && (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold group cursor-default">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-white transition-all duration-300 group-hover:tracking-wider">
                      {currentAlbum.name}
                    </span>
                  </h1>
                  <div className="flex items-center gap-2 text-gray-400 mt-4">
                    <Calendar size={18} />
                    <span>{formatDate(currentAlbum.date)}</span>
                  </div>
                  {currentAlbum.description && (
                    <p className="text-gray-400 mt-2 text-lg max-w-2xl">{currentAlbum.description}</p>
                  )}
                </>
              )}
            </div>

            {/* Photos Grid */}
            {loading ? (
              <div className="text-center text-gray-400 py-20">Loading photos...</div>
            ) : !currentAlbum?.photos || currentAlbum.photos.length === 0 ? (
              <div className="text-center text-gray-400 py-20">
                <p className="text-xl">No photos in this album yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentAlbum.photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    onClick={() => openLightbox(index)}
                    className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg bg-gray-900 hover:scale-105 transition-all duration-300"
                  >
                    <img
                      src={photo.url}
                      alt={photo.title || `Photo ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {photo.title && (
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-white font-semibold group-hover:text-purple-400 transition-colors duration-300">
                            {photo.title}
                          </h3>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Lightbox Modal */}
        {selectedIndex !== null && currentAlbum?.photos && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 text-white hover:text-purple-500 transition-colors"
            >
              <X size={32} />
            </button>

            {/* Navigation */}
            {currentAlbum.photos.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 z-10 text-white hover:text-purple-500 transition-colors"
                >
                  <ChevronLeft size={48} />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 z-10 text-white hover:text-purple-500 transition-colors"
                >
                  <ChevronRight size={48} />
                </button>
              </>
            )}

            {/* Content */}
            <div className="max-w-6xl max-h-[90vh] mx-4 flex flex-col">
              <div className="flex-1 flex items-center justify-center mb-4">
                <img
                  src={currentAlbum.photos[selectedIndex].url}
                  alt={currentAlbum.photos[selectedIndex].title || 'Photo'}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              </div>

              {currentAlbum.photos[selectedIndex].title && (
                <div className="bg-black/60 backdrop-blur-sm rounded-lg p-6 text-center">
                  <h3 className="text-2xl font-bold text-purple-500">
                    {currentAlbum.photos[selectedIndex].title}
                  </h3>
                </div>
              )}

              <div className="text-center text-gray-400 mt-4">
                {selectedIndex + 1} / {currentAlbum.photos.length}
              </div>
            </div>
          </div>
        )}
      </PageLayout>
      <Footer />
    </>
  );
};

export default PartyGallery;
